import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0B0B0E] px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-emerald-300/60";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — I'll get back to you within 24 hours.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8" data-testid="contact-page">
      <div className="grid gap-12 lg:grid-cols-2">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">Get in touch</p>
          <h1 className="font-heading mt-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">Contact</h1>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-zinc-400 sm:text-base">
            Custom beat ideas, mixing quotes, video concepts, collabs — drop the details here and I'll reply within a day.
          </p>
          <div className="mt-10 space-y-4 text-sm">
            <div className="rounded-2xl border border-white/10 bg-[#141419] p-5">
              <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">Email</p>
              <a href="mailto:prod.rushibusiness@gmail.com" data-testid="contact-email-link"
                className="mt-2 block text-zinc-300 transition-colors hover:text-emerald-200">prod.rushibusiness@gmail.com</a>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#141419] p-5">
              <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">Studio</p>
              <p className="mt-2 text-zinc-300">Blackroom Studios<br />Atlanta, Georgia<br />Mon–Fri 3PM–12AM · Sat–Sun 4PM–12AM</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#141419] p-5">
              <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">Response time</p>
              <p className="mt-2 text-zinc-300">Within 24 hours, 7 days a week</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#141419] p-5" data-testid="contact-socials-card">
              <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">Follow the work</p>
              <div className="mt-3 space-y-1.5 text-sm">
                <a href="https://www.instagram.com/prod.rushi/" target="_blank" rel="noopener noreferrer" data-testid="contact-social-instagram" className="block text-zinc-300 transition-colors hover:text-emerald-200">Instagram — @prod.rushi</a>
                <a href="https://www.youtube.com/@PRODRUSHI" target="_blank" rel="noopener noreferrer" data-testid="contact-social-youtube" className="block text-zinc-300 transition-colors hover:text-emerald-200">YouTube — @PRODRUSHI</a>
                <a href="https://soundcloud.com/producedbyrushi" target="_blank" rel="noopener noreferrer" data-testid="contact-social-soundcloud" className="block text-zinc-300 transition-colors hover:text-emerald-200">SoundCloud — producedbyrushi</a>
                <a href="https://open.spotify.com/artist/1fVSxNbesvw20EEMIUc4OU" target="_blank" rel="noopener noreferrer" data-testid="contact-social-spotify" className="block text-zinc-300 transition-colors hover:text-emerald-200">Spotify — PRODRUSHI</a>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="space-y-4 rounded-3xl border border-white/10 bg-[#141419] p-6 sm:p-8" data-testid="contact-form">
            <input required placeholder="Your name" value={form.name} data-testid="contact-name-input"
              onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            <input required type="email" placeholder="Your email" value={form.email} data-testid="contact-email-input"
              onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            <textarea required rows={7} placeholder="Tell me about your project…" value={form.message} data-testid="contact-message-input"
              onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} />
            <button
              type="submit" disabled={sending} data-testid="contact-submit-button"
              className="flex w-full items-center justify-center gap-3 rounded-full bg-emerald-300 py-4 text-sm font-bold uppercase tracking-wider text-black transition-all hover:bg-emerald-200 disabled:opacity-50"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
              {sending ? "Sending…" : "Send Message"}
            </button>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
